import React, { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiCheck, FiCreditCard, FiCalendar, FiClock, FiFileText } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

const t = (en: string, ar: string, lang: DashLang) => lang === 'ar' ? ar : en;

const PLANS = [
  {
    key: 'basic',
    yearlyPrice: 99,
    name: { en: 'Starter', ar: 'البداية' },
    features: {
      en: ['Up to 100 products', '1 online store', 'Arabic & English', 'Free Cartz subdomain', 'Orders & inventory', 'Payments & delivery', '0% sales commission'],
      ar: ['حتى 100 منتج', 'متجر إلكتروني واحد', 'عربي وإنجليزي', 'نطاق فرعي مجاني', 'الطلبات والمخزون', 'الدفع والتوصيل', 'بدون عمولة على المبيعات'],
    },
  },
  {
    key: 'professional',
    yearlyPrice: 199,
    popular: true,
    name: { en: 'Growth', ar: 'النمو' },
    features: {
      en: ['Unlimited products', 'Custom domain', 'Cartz POS included', 'Up to 3 branches', 'Team roles & permissions', 'Advanced reports', 'Priority support'],
      ar: ['منتجات غير محدودة', 'نطاق مخصص', 'نقطة بيع POS', 'حتى 3 فروع', 'إدارة الفريق والصلاحيات', 'تقارير متقدمة', 'دعم أولوية'],
    },
  },
  {
    key: 'enterprise',
    yearlyPrice: 299,
    name: { en: 'Omnichannel', ar: 'متعدد القنوات' },
    features: {
      en: ['Everything in Growth', 'Up to 3 stores', 'Unlimited branches', 'Web, iOS & Android POS', 'Unified online & branch stock', 'API & integrations', 'Dedicated support'],
      ar: ['كل مزايا النمو', 'حتى 3 متاجر', 'فروع غير محدودة', 'POS للويب وiOS وAndroid', 'مخزون موحد للمتجر والفروع', 'واجهة API وتكاملات', 'دعم مخصص'],
    },
  },
];

function calcPrice(yearlyPrice: number, cycle: 'monthly' | 'quarterly' | 'yearly') {
  if (cycle === 'yearly') return +yearlyPrice.toFixed(3);
  const monthlyPrice = yearlyPrice / 10; // annual billing includes two months free
  if (cycle === 'quarterly') return +(monthlyPrice * 3).toFixed(3);
  return +monthlyPrice.toFixed(3);
}

const CYCLE_LABELS: Record<string, { en: string; ar: string }> = {
  monthly: { en: '/mo', ar: '/شهر' },
  quarterly: { en: '/3 mo', ar: '/3 أشهر' },
  yearly: { en: '/yr', ar: '/سنة' },
};

const PLAN_NAMES: Record<string, { en: string; ar: string }> = {
  basic: { en: 'Starter', ar: 'البداية' },
  professional: { en: 'Growth', ar: 'النمو' },
  enterprise: { en: 'Omnichannel', ar: 'متعدد القنوات' },
};

const METHOD_LABELS: Record<string, { en: string; ar: string }> = {
  knet: { en: 'KNET (Tahseeel)', ar: 'كي نت (تحصيل)' },
  creditcard: { en: 'Visa / MC', ar: 'فيزا / ماستركارد' },
  paypal: { en: 'PayPal', ar: 'باي بال' },
  cbk_knet: { en: 'KNET (CBK)', ar: 'كي نت (CBK)' },
};

const STATUS_LABELS: Record<string, { en: string; ar: string; color: string }> = {
  paid: { en: 'Paid', ar: 'مدفوع', color: '#10b981' },
  pending: { en: 'Pending', ar: 'قيد الانتظار', color: '#f59e0b' },
  failed: { en: 'Failed', ar: 'فشل', color: '#ef4444' },
};

export default function SubscriptionPage() {
  const { store, reloadStore, lang } = useOutletContext<{ store: any; reloadStore: () => void; lang: DashLang }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'knet' | 'creditcard'>('knet');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'quarterly' | 'yearly'>('yearly');
  const [subscribing, setSubscribing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState<any>(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const currentPlan = store?.plan || null;

  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      toast.success(t('Payment successful! Plan activated.', 'تم الدفع بنجاح! تم تفعيل الخطة.', lang));
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
      // Reload store data to get updated plan, then redirect to domain setup
      reloadStore();
      if (!store?.custom_domain) {
        setTimeout(() => navigate('/dashboard/settings?section=domain'), 2000);
      }
    } else if (payment === 'failed') {
      toast.error(t('Payment failed. Please try again.', 'فشل الدفع. حاول مرة أخرى.', lang));
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!store?.id) return;
    setLoading(true);
    api.get(`/subscription?store_id=${store.id}`).then((res) => {
      setSubscription(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [store?.id]);

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const res = await api.post('/subscribe', {
        store_id: store?.id, plan: selectedPlan, billing_cycle: billingCycle, payment_method: paymentMethod,
        promo_code: promoResult?.valid ? promoCode : undefined,
      });
      if (res.data.payment_required && res.data.payment_url) {
        window.location.href = res.data.payment_url;
        return;
      } else {
        toast.success(t('Plan activated!', 'تم تفعيل الخطة!', lang));
        setShowPaymentModal(false);
        reloadStore();
        // Redirect to domain setup if no custom domain
        if (!store?.custom_domain && ['professional', 'enterprise'].includes(selectedPlan)) {
          setTimeout(() => navigate('/dashboard/settings?section=domain'), 1500);
          return;
        }
        window.location.reload();
        return;
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Failed to subscribe', 'فشل الاشتراك', lang));
      setSubscribing(false);
    }
  };

  const handleStartTrial = async (plan: string) => {
    setSubscribing(true);
    try {
      await api.post('/subscription/start-trial', { plan, store_id: store?.id });
      toast.success(t('Free trial started!', 'تم بدء التجربة المجانية!', lang));
      window.location.reload();
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Failed to start trial', 'فشل بدء التجربة', lang));
      setSubscribing(false);
    }
  };

  if (loading) return <div className="empty-state"><p>{dt('loading', lang)}</p></div>;

  const payments = subscription?.payments || [];

  // Plan selection screen for new users
  if (subscription?.needs_plan_selection) {
    return (
      <div>
        <div style={{ textAlign: 'center', padding: '32px 0 24px' }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px' }}>
            {t('Welcome! Choose your plan', 'مرحباً! اختر خطتك', lang)}
          </h1>
          <p style={{ color: 'var(--gray-500)', fontSize: 16, margin: 0 }}>
            {t('All plans include a 15-day free trial with full features — no credit card required', 'جميع الخطط تتضمن 15 يوم تجربة مجانية بكامل المزايا — بدون بطاقة ائتمانية', lang)}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          {PLANS.map((plan) => {
            const yearly = calcPrice(plan.yearlyPrice, 'yearly');
            const monthly = calcPrice(plan.yearlyPrice, 'monthly');
            return (
              <div key={plan.key} className="card" style={{
                padding: 28,
                border: plan.popular ? '2px solid var(--primary)' : '1px solid var(--gray-200)',
                position: 'relative',
                display: 'flex', flexDirection: 'column',
              }}>
                {plan.popular && (
                  <div style={{
                    position: 'absolute', top: -12, insetInlineStart: 20,
                    background: 'var(--primary)', color: 'white',
                    padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  }}>
                    {t('Most Popular', 'الأكثر شعبية', lang)}
                  </div>
                )}

                <h3 style={{ margin: '8px 0 4px', fontSize: 20, fontWeight: 700 }}>{plan.name[lang]}</h3>
                <div style={{ fontSize: 32, fontWeight: 800, margin: '8px 0 4px' }}>
                  {yearly} <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--gray-500)' }}>{t('KD /yr', 'د.ك /سنة', lang)}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 12 }}>
                  {t('or', 'أو', lang)} {monthly.toFixed(3)} {t('KD /mo', 'د.ك /شهر', lang)}
                </div>

                <div style={{ marginBottom: 14, textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', padding: '4px 14px', borderRadius: 20,
                    background: '#dcfce7', color: '#16a34a', fontSize: 12, fontWeight: 600,
                  }}>
                    {t('15 days free trial', '15 يوم تجربة مجانية', lang)}
                  </span>
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', flex: 1 }}>
                  {plan.features[lang].map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 14, color: 'var(--gray-600)' }}>
                      <FiCheck style={{ color: '#10b981', flexShrink: 0 }} /> {f}
                    </li>
                  ))}
                </ul>

                <button
                  className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline'} btn-lg`}
                  style={{ width: '100%' }}
                  disabled={subscribing}
                  onClick={() => handleStartTrial(plan.key)}
                >
                  {subscribing ? '...' : t('Start 15-Day Free Trial', 'ابدأ التجربة المجانية 15 يوم', lang)}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1><FiCreditCard style={{ marginInlineEnd: 8 }} /> {t('Subscription Plan', 'خطة الاشتراك', lang)}</h1>
      </div>

      {/* Trial banner */}
      {subscription?.is_on_trial && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
          border: '1px solid #f59e0b',
          borderRadius: 12, padding: '16px 24px', marginBottom: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <strong style={{ color: '#92400e', fontSize: 16 }}>
              {t('Free Trial', 'فترة تجريبية مجانية', lang)} — {subscription.trial_days_remaining} {t('days remaining', 'يوم متبقي', lang)}
            </strong>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#78350f' }}>
              {t(
                'Subscribe now to keep all features and get a free domain!',
                'اشترك الآن للحفاظ على جميع المزايا والحصول على دومين مجاني!',
                lang
              )}
            </p>
          </div>
          <button
            className="btn btn-primary"
            style={{ background: '#f59e0b', borderColor: '#f59e0b', whiteSpace: 'nowrap' }}
            onClick={() => { setSelectedPlan(currentPlan || 'professional'); setShowPaymentModal(true); }}
          >
            {t('Subscribe Now', 'اشترك الآن', lang)}
          </button>
        </div>
      )}

      {/* Trial expired banner */}
      {subscription?.trial_ends_at && !subscription?.is_on_trial && !subscription?.subscription && (
        <div style={{
          background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
          border: '1px solid #ef4444',
          borderRadius: 12, padding: '16px 24px', marginBottom: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <strong style={{ color: '#991b1b', fontSize: 16 }}>
              {t('Your free trial has expired', 'انتهت الفترة التجريبية المجانية', lang)}
            </strong>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#7f1d1d' }}>
              {t(
                'Subscribe to restore full access. Your data is safe!',
                'اشترك لاستعادة الوصول الكامل. بياناتك آمنة!',
                lang
              )}
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => { setSelectedPlan(currentPlan || 'professional'); setShowPaymentModal(true); }}
          >
            {t('Subscribe Now', 'اشترك الآن', lang)}
          </button>
        </div>
      )}

      {/* Trial info (no paid subscription yet) */}
      {subscription?.is_on_trial && !subscription?.subscription && subscription?.plan && (
        <div className="card" style={{ padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
            {t('Current Plan', 'الخطة الحالية', lang)}
          </h3>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiCreditCard style={{ color: 'var(--primary)' }} />
              <div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Plan', 'الخطة', lang)}</div>
                <strong>{PLAN_NAMES[subscription.plan]?.[lang] || subscription.plan}</strong>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiClock style={{ color: '#f59e0b' }} />
              <div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Trial Ends', 'تنتهي التجربة', lang)}</div>
                <strong>{subscription.trial_ends_at ? new Date(subscription.trial_ends_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US') : '-'}</strong>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiCalendar style={{ color: '#f59e0b' }} />
              <div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Days Remaining', 'الأيام المتبقية', lang)}</div>
                <strong style={{ color: (subscription.trial_days_remaining || 0) <= 3 ? '#ef4444' : '#f59e0b' }}>
                  {subscription.trial_days_remaining} {t('days', 'يوم', lang)}
                </strong>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} />
              <div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Status', 'الحالة', lang)}</div>
                <strong style={{ color: '#f59e0b' }}>{t('Trial', 'تجربة مجانية', lang)}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Current plan info */}
      {subscription?.subscription && (
        <div className="card" style={{ padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
            {t('Current Subscription', 'الاشتراك الحالي', lang)}
          </h3>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiCreditCard style={{ color: 'var(--primary)' }} />
              <div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Plan', 'الخطة', lang)}</div>
                <strong>{PLAN_NAMES[subscription.subscription.plan]?.[lang] || subscription.subscription.plan}</strong>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiCalendar style={{ color: 'var(--primary)' }} />
              <div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Started', 'تاريخ البدء', lang)}</div>
                <strong>{subscription.subscription.starts_at ? new Date(subscription.subscription.starts_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US') : '-'}</strong>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiClock style={{ color: subscription.subscription.expires_at && new Date(subscription.subscription.expires_at) < new Date() ? '#ef4444' : 'var(--primary)' }} />
              <div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Expires', 'تاريخ الانتهاء', lang)}</div>
                <strong>{subscription.subscription.expires_at ? new Date(subscription.subscription.expires_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US') : '-'}</strong>
              </div>
            </div>
            {subscription.subscription.transaction_id && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiFileText style={{ color: 'var(--primary)' }} />
                <div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Transaction ID', 'رقم المعاملة', lang)}</div>
                  <strong style={{ fontSize: 13 }}>{subscription.subscription.transaction_id}</strong>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%',
                background: subscription.is_active ? '#10b981' : '#ef4444',
              }} />
              <div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Status', 'الحالة', lang)}</div>
                <strong style={{ color: subscription.is_active ? '#10b981' : '#ef4444' }}>
                  {subscription.is_active ? t('Active', 'نشط', lang) : t('Inactive', 'غير نشط', lang)}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscribe / Upgrade button */}
      {!subscription?.is_on_trial && !subscription?.subscription && (
        <div className="card" style={{ padding: 32, textAlign: 'center', marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('No active subscription', 'لا يوجد اشتراك نشط', lang)}</h3>
          <p style={{ color: 'var(--gray-500)', margin: '0 0 20px', fontSize: 14 }}>{t('Subscribe to activate your store', 'اشترك لتفعيل متجرك', lang)}</p>
          <button className="btn btn-primary btn-lg" onClick={() => { setSelectedPlan(currentPlan || 'professional'); setShowPaymentModal(true); }}>
            {t('Choose a Plan & Subscribe', 'اختر خطة واشترك', lang)}
          </button>
        </div>
      )}

      {subscription?.subscription && (
        <div style={{ marginBottom: 24 }}>
          <button className="btn btn-primary" onClick={() => { setSelectedPlan(currentPlan || 'professional'); setShowPaymentModal(true); }}>
            {t('Renew / Change Plan', 'تجديد / تغيير الخطة', lang)}
          </button>
        </div>
      )}

      {/* Payment History */}
      {payments.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header">
            <h2>{t('Payment History', 'سجل المدفوعات', lang)}</h2>
          </div>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('Plan', 'الخطة', lang)}</th>
                  <th>{t('Amount', 'المبلغ', lang)}</th>
                  <th>{t('Method', 'الطريقة', lang)}</th>
                  <th>{t('Transaction ID', 'رقم المعاملة', lang)}</th>
                  <th>{t('Status', 'الحالة', lang)}</th>
                  <th>{t('Date', 'التاريخ', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p: any, idx: number) => {
                  const statusInfo = STATUS_LABELS[p.payment_status] || { en: p.payment_status, ar: p.payment_status, color: '#94a3b8' };
                  return (
                    <tr key={p.id}>
                      <td>{idx + 1}</td>
                      <td><strong>{PLAN_NAMES[p.plan]?.[lang] || p.plan}</strong></td>
                      <td>{p.amount > 0 ? `${p.amount} ${t('KD', 'د.ك', lang)}` : t('Free', 'مجاني', lang)}</td>
                      <td>{METHOD_LABELS[p.payment_method]?.[lang] || p.payment_method}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{p.transaction_id || '-'}</td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                          fontSize: 12, fontWeight: 600,
                          background: `${statusInfo.color}18`, color: statusInfo.color,
                        }}>
                          {t(statusInfo.en, statusInfo.ar, lang)}
                        </span>
                      </td>
                      <td>{p.created_at ? new Date(p.created_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Modal with Plan Selection */}
      {showPaymentModal && (() => {
        const selPlan = PLANS.find(p => p.key === selectedPlan);
        const price = selPlan ? calcPrice(selPlan.yearlyPrice, billingCycle) : 0;
        const promoDiscount = promoResult?.valid ? promoResult.discount : 0;

        // Proration: calculate credit from current active subscription
        let proratedCredit = 0;
        let proratedDays = 0;
        let proratedTotal = 0;
        const activeSub = subscription?.subscription;
        const isUpgrade = activeSub && selectedPlan !== activeSub.plan;
        if (isUpgrade && activeSub.expires_at) {
          const expiresAt = new Date(activeSub.expires_at);
          const now = new Date();
          proratedDays = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
          const paidAmount = parseFloat(activeSub.amount) || 0;
          const totalDays = activeSub.billing_cycle === 'yearly' ? 365 : activeSub.billing_cycle === 'quarterly' ? 90 : 30;
          const dailyRate = totalDays > 0 ? paidAmount / totalDays : 0;
          proratedCredit = +(dailyRate * proratedDays).toFixed(3);
          proratedTotal = Math.max(0, +(price - promoDiscount - proratedCredit).toFixed(3));
        }

        const finalPrice = isUpgrade ? proratedTotal : Math.max(0, +(price - promoDiscount).toFixed(3));

        const validatePromo = async () => {
          if (!promoCode.trim()) return;
          setPromoLoading(true);
          try {
            const res = await api.post('/subscription/validate-promo', {
              code: promoCode, plan: selectedPlan, billing_cycle: billingCycle, store_id: store?.id,
            });
            setPromoResult(res.data);
          } catch {
            setPromoResult({ valid: false, message: t('Error validating code', 'خطأ في التحقق من الكود', lang) });
          } finally { setPromoLoading(false); }
        };

        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !subscribing && setShowPaymentModal(false)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, maxWidth: 520, width: '95%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px', fontSize: 20 }}>
              {t('Subscribe', 'اشتراك', lang)}
            </h2>

            {/* Plan selector */}
            <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>{t('Select Plan', 'اختر الخطة', lang)}</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {PLANS.map((plan) => {
                const isSelected = selectedPlan === plan.key;
                const isCur = activeSub?.plan === plan.key;
                const yearly = calcPrice(plan.yearlyPrice, 'yearly');
                const isHigher = PLANS.findIndex(p => p.key === plan.key) > PLANS.findIndex(p => p.key === activeSub?.plan);
                return (
                  <button
                    key={plan.key}
                    onClick={() => { if (!isCur) { setSelectedPlan(plan.key); setPromoResult(null); } }}
                    disabled={isCur}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 16px', borderRadius: 10,
                      cursor: isCur ? 'default' : 'pointer',
                      opacity: isCur ? 0.5 : 1,
                      border: isSelected && !isCur ? '2px solid var(--primary)' : '2px solid var(--gray-200)',
                      background: isSelected && !isCur ? 'var(--primary-light, #eef2ff)' : isCur ? 'var(--gray-50)' : 'white',
                      textAlign: 'inherit',
                    }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>
                        {plan.name[lang]}
                        {isCur && <span style={{ fontSize: 11, marginInlineStart: 8, padding: '2px 8px', borderRadius: 10, background: '#10b98118', color: '#10b981', fontWeight: 600 }}>{t('Current', 'الحالية', lang)}</span>}
                        {!isCur && activeSub && isHigher && <span style={{ fontSize: 11, marginInlineStart: 8, padding: '2px 8px', borderRadius: 10, background: '#3b82f618', color: '#3b82f6', fontWeight: 600 }}>{t('Upgrade', 'ترقية', lang)}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>
                        {plan.features[lang].slice(0, 3).join(' · ')}
                      </div>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 16, whiteSpace: 'nowrap' }}>
                      {yearly} <span style={{ fontSize: 11, fontWeight: 400 }}>{t('KD/yr', 'د.ك/سنة', lang)}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Billing cycle selector */}
            <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>{t('Billing Cycle', 'دورة الفوترة', lang)}</h4>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {(['monthly', 'quarterly', 'yearly'] as const).map((cycle) => {
                const labels = {
                  monthly: { en: 'Monthly', ar: 'شهري' },
                  quarterly: { en: 'Quarterly', ar: 'ربع سنوي' },
                  yearly: { en: 'Yearly', ar: 'سنوي' },
                };
                const isSelected = billingCycle === cycle;
                const p = selPlan ? calcPrice(selPlan.yearlyPrice, cycle) : 0;
                return (
                  <button
                    key={cycle}
                    onClick={() => { setBillingCycle(cycle); setPromoResult(null); }}
                    style={{
                      flex: 1, padding: '10px 6px', borderRadius: 10, cursor: 'pointer',
                      border: isSelected ? '2px solid var(--primary)' : '2px solid var(--gray-200)',
                      background: isSelected ? 'var(--primary-light, #eef2ff)' : 'white',
                      textAlign: 'center', fontSize: 12, fontWeight: 600, position: 'relative',
                    }}>
                    <div>{labels[cycle][lang]}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{p.toFixed(3)} {t('KD', 'د.ك', lang)}</div>
                    {cycle === 'yearly' && (
                      <span style={{ position: 'absolute', top: -8, insetInlineEnd: -4, background: '#16a34a', color: 'white', fontSize: 9, padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>
                        -5%
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Promo Code */}
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>{t('Promo Code', 'كود الخصم', lang)}</h4>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-input"
                  value={promoCode}
                  onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); }}
                  onKeyDown={e => e.key === 'Enter' && validatePromo()}
                  placeholder={t('Enter code', 'أدخل الكود', lang)}
                  dir="ltr"
                  style={{ flex: 1, fontFamily: 'monospace', textTransform: 'uppercase', textAlign: 'left' }}
                />
                <button className="btn btn-outline" onClick={validatePromo} disabled={promoLoading || !promoCode.trim()}>
                  {promoLoading ? '...' : t('Apply', 'تطبيق', lang)}
                </button>
              </div>
              {promoResult && (
                <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: promoResult.valid ? '#16a34a' : '#ef4444' }}>
                  {promoResult.valid
                    ? `${t('Discount', 'خصم', lang)}: -${promoResult.discount.toFixed(3)} ${t('KD', 'د.ك', lang)}`
                    : t('Invalid or expired promo code', 'كود الخصم غير صالح أو منتهي الصلاحية', lang)}
                </div>
              )}
            </div>

            {/* Total */}
            <div style={{ padding: '14px 16px', background: 'var(--gray-50)', borderRadius: 8, marginBottom: 16 }}>
              {/* Breakdown */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--gray-500)', marginBottom: 4 }}>
                <span>{selPlan?.name[lang]} ({CYCLE_LABELS[billingCycle]?.[lang]})</span>
                <span>{price.toFixed(3)} {t('KD', 'د.ك', lang)}</span>
              </div>

              {promoDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#16a34a', marginBottom: 4 }}>
                  <span>{t('Promo discount', 'خصم كود', lang)}</span>
                  <span>-{promoDiscount.toFixed(3)} {t('KD', 'د.ك', lang)}</span>
                </div>
              )}

              {isUpgrade && proratedCredit > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#3b82f6', marginBottom: 4 }}>
                  <span>{t('Credit for remaining', 'رصيد متبقي', lang)} ({proratedDays} {t('days', 'يوم', lang)})</span>
                  <span>-{proratedCredit.toFixed(3)} {t('KD', 'د.ك', lang)}</span>
                </div>
              )}

              <div style={{ borderTop: '1px solid var(--gray-200)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t('Total', 'الإجمالي', lang)}</span>
                <span style={{ fontSize: 24, fontWeight: 800, color: (promoDiscount > 0 || proratedCredit > 0) ? '#16a34a' : undefined }}>
                  {finalPrice.toFixed(3)} <span style={{ fontSize: 13, fontWeight: 400 }}>{t('KD', 'د.ك', lang)}</span>
                </span>
              </div>
            </div>

            {/* Payment method */}
            <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>{t('Payment Method', 'طريقة الدفع', lang)}</h4>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {(['knet', 'creditcard'] as const).map((method) => {
                const isSelected = paymentMethod === method;
                return (
                  <button
                    key={method}
                    onClick={() => setPaymentMethod(method)}
                    style={{
                      flex: 1, padding: '12px 10px', borderRadius: 10, cursor: 'pointer',
                      border: isSelected ? '2px solid var(--primary)' : '2px solid var(--gray-200)',
                      background: isSelected ? 'var(--primary-light, #eef2ff)' : 'white',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    }}>
                    {method === 'knet' ? (
                      <img src="https://portal.roolink.net/assets/img/knet-logo.png" alt="KNET" style={{ height: 28 }} />
                    ) : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <img src="https://portal.roolink.net/assets/img/visa-logo.png" alt="Visa" style={{ height: 20 }} />
                        <img src="https://portal.roolink.net/assets/img/mastercard-logo.png" alt="MC" style={{ height: 22 }} />
                      </div>
                    )}
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {method === 'knet' ? t('KNET', 'كي نت', lang) : t('Visa / Mastercard', 'فيزا / ماستركارد', lang)}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', opacity: subscribing ? 0.6 : 1, pointerEvents: subscribing ? 'none' : 'auto' }}
              onClick={() => { if (!subscribing) handleSubscribe(); }}
              disabled={subscribing || !selectedPlan}>
              {subscribing
                ? t('Processing payment...', 'جاري معالجة الدفع...', lang)
                : t('Pay Now', 'ادفع الآن', lang)}
            </button>
            <button
              style={{ width: '100%', marginTop: 8, padding: 10, background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer' }}
              onClick={() => setShowPaymentModal(false)}
              disabled={subscribing}>
              {t('Cancel', 'إلغاء', lang)}
            </button>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
